import { useMutation, useQueryClient } from '@tanstack/react-query'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import type { UpdateWatchlistModel, WatchlistModel } from '@model/DxTrade.types'

import { normalizeSymbols } from '../helpers'

type UpdateWatchlistVariables = {
  id: number
  payload: UpdateWatchlistModel
}

const MUTATION_KEY = ['updateWatchlist']

const normalizePayload = (payload: UpdateWatchlistModel): UpdateWatchlistModel => ({
  ...payload,
  symbols: payload.symbols ? normalizeSymbols(payload.symbols) : undefined,
})

export const useUpdateWatchlist = () => {
  const apiClient = useApiClient(ClientApiClient)
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: MUTATION_KEY,
    scope: { id: 'updateWatchlist' },
    mutationFn: ({ id, payload }: UpdateWatchlistVariables): Promise<WatchlistModel> => {
      const allCached = queryClient.getQueriesData<WatchlistModel[]>({
        queryKey: ['trade', 'dxtrade', 'watchlists'],
      })
      const existing = allCached
        .flatMap(([, data]) => data ?? [])
        .find((watchlist) => watchlist.id === id)
      const payloadWithOrder: UpdateWatchlistModel =
        existing?.orderValue != null ? { ...payload, orderValue: existing.orderValue } : payload
      return apiClient.updateWatchlist(id, normalizePayload(payloadWithOrder))
    },
    onMutate: async ({ id, payload }) => {
      const normalizedPayload = normalizePayload(payload)

      await queryClient.cancelQueries({ queryKey: ['trade', 'dxtrade', 'watchlists'] })

      const previousData = queryClient.getQueriesData<WatchlistModel[]>({
        queryKey: ['trade', 'dxtrade', 'watchlists'],
      })

      queryClient.setQueriesData<WatchlistModel[]>(
        { queryKey: ['trade', 'dxtrade', 'watchlists'] },
        (old) =>
          old?.map((watchlist) =>
            watchlist.id === id ? { ...watchlist, symbols: normalizedPayload.symbols } : watchlist
          )
      )

      return { previousData }
    },
    onError: (_err, _variables, context) => {
      context?.previousData.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data)
      })
    },
    onSettled: () => {
      if (queryClient.isMutating({ mutationKey: MUTATION_KEY }) <= 1) {
        queryClient.invalidateQueries({ queryKey: ['trade', 'dxtrade', 'watchlists'] })
      }
    },
  })
}
